// /reports/input-tax — Input Tax Summary (QA PU4-010, docs/purchase-gaps-plan.md §5.8)
//
// Tax paid on posted supplier bills by month and rate, less debit notes, in the
// tenant currency — the input tax credit figure. India splits CGST / SGST /
// IGST; a VAT tenant gets one VAT column. GSTR-2B matching is out of scope (G9).

import { useState } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  KpiIcons,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { InputTaxRow, ReportKpiTile } from '@/features/reports';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';

type Row = InputTaxRow & { id: string };

export function InputTaxReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const isGst = tax.regime === 'GST_INDIA';

  const { data, isLoading, error } = useReportQuery({
    key: 'input-tax',
    filter,
    queryFn: (f) => reportsApi.getInputTax(f),
  });

  const rows: Row[] = (data?.rows ?? []).map((r) => ({ ...r, id: `${r.month}-${r.rate}` }));
  const money = (get: (r: Row) => number) => (_v: unknown, r: Row) => (
    <span className={`tabular-nums ${get(r) < 0 ? 'text-danger-600 dark:text-danger-400' : ''}`}>{formatPaise(get(r), currency)}</span>
  );
  const splitCols: { key: 'cgst' | 'sgst' | 'igst' | 'vat'; header: string }[] = isGst
    ? [{ key: 'cgst', header: 'CGST' }, { key: 'sgst', header: 'SGST' }, { key: 'igst', header: 'IGST' }]
    : [{ key: 'vat', header: tax.taxName }];

  const columns: ColumnDef<Row>[] = [
    { key: 'month', header: 'Month', render: (_v, r) => <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{r.month}</span> },
    { key: 'rate', header: 'Rate', numeric: true, render: (_v, r) => <span className="inline-flex rounded-full bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 text-xs font-semibold text-teal-700 dark:text-teal-300">{r.rate}%</span> },
    { key: 'taxable', header: 'Taxable', numeric: true, render: money((r) => r.taxable) },
    ...splitCols.map((c): ColumnDef<Row> => ({ key: c.key, header: c.header, numeric: true, render: money((r) => r[c.key]) })),
    { key: 'tax', header: `Total ${tax.taxName}`, numeric: true, render: (_v, r) => <span className="text-sm font-bold tabular-nums">{formatPaise(r.tax, currency)}</span> },
  ];

  const kpis: ReportKpiTile[] = data
    ? [
        { id: 'tax', title: `Input ${tax.taxName}`, value: formatPaise(data.totals.tax, currency), icon: KpiIcons.percent, tone: 'teal' },
        { id: 'taxable', title: 'Taxable purchases', value: formatPaise(data.totals.taxable, currency), icon: KpiIcons.cart, tone: 'indigo' },
        ...(isGst
          ? [
              { id: 'cgst-sgst', title: 'CGST + SGST', value: formatPaise(data.totals.cgst + data.totals.sgst, currency), icon: KpiIcons.receipt, tone: 'success' as const },
              { id: 'igst', title: 'IGST', value: formatPaise(data.totals.igst, currency), icon: KpiIcons.file, tone: 'violet' as const },
            ]
          : []),
      ]
    : [];

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      window.print();
      return;
    }
    if (fmt === 'csv') {
      const subunit = getSubunitMultiplier(currency);
      const decimals = Math.round(Math.log10(subunit));
      const amount = (v: number) => (v / subunit).toFixed(decimals);
      downloadCsv(
        `input-tax-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Month', get: (r: Row) => r.month },
          { header: 'Rate %', get: (r: Row) => r.rate },
          { header: `Taxable (${currency})`, get: (r: Row) => amount(r.taxable) },
          ...splitCols.map((c) => ({ header: `${c.header} (${currency})`, get: (r: Row) => amount(r[c.key]) })),
          { header: `Total ${tax.taxName} (${currency})`, get: (r: Row) => amount(r.tax) },
        ],
        rows,
      );
      return;
    }
    shareReportLink(fmt, `Orio360 Input ${tax.taxName}`, `Input ${tax.taxName} ${formatPaise(data?.totals.tax ?? 0, currency)}`);
  };

  return (
    <ReportPageShell
      title="Input Tax Summary"
      subtitle={`${tax.taxName} paid on purchases by month and rate, less debit notes.`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Input Tax' }]}
      accent="teal-success"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow tiles={kpis} />
          <ReportTable title="By month and rate" columns={columns} data={rows} emptyTitle="No input tax in this period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
